<template>
	<div class="col-10-md mt-250-md mt-100-smo">
		<div
			v-if="leadCaptured"
			:data-test="componentId ? `${componentId}-ocf-form-submit-success` : 'ocf-form-submit-success'"
			class="text-color-brand-primary"
		>
			Thanks for your interest. You're on the waitlist.
		</div>
		<form v-else name="growthTechLeadCapture" class="lead-capture position-relative" @submit.prevent>
			<div class="display-flex-lg align-items-flex-start flex-wrap-wrap lead-capture__fieldset">
				<InputField
					:field="v$.email"
					field-name="Email"
					aria-labelledby="email"
					class="flex-1-lg mr-50-lg overflow-hidden-lg"
				>
					<template #label>
						<span id="email" class="show-for-sr">Email address</span>
					</template>
					<template #input>
						<input
							v-model="v$.email.$model"
							class="input"
							placeholder="Email address"
							name="email"
							type="email"
							:data-test="componentId ? `${componentId}-email-input` : 'email-input'"
						/>
						<p class="body-sm text-color-gray-40 mt-25 text-align-left"> Accredited investors only</p>
					</template>
				</InputField>
				<div class="email-submit__button--container text-align-right-lg mt-100-smo pt-50 pt-0-lg mt-25-lg">
					<SubmitButton
						:data-test="componentId ? `${componentId}-join-ocf-waitlist` : 'join-ocf-waitlist'"
						:class="[{ 'width-100': !appStore.isDesktop }, 'lead-capture__button', props.buttonColor]"
						:is-working="isWorking"
						:v$="v$"
						@sb-click="submitEmail"
					>
						Join the waitlist
					</SubmitButton>
				</div>
			</div>
		</form>
	</div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { email, required } from '@vuelidate/validators';
import { getItemFromLocalStorage, setItemInLocalStorage, setItemInSessionStorage } from '@utils/web-storage';
import { NonShoppingLeadCaptureResponse, OcfLeadParams } from 'types/layout/lead';
import { useRoute, useRouter } from 'vue-router';
import { captureNonShoppingLead } from '@api/lead-capture';
import { getDistinctId } from '@utils/analytics';
import InputField from '@components/form/input-field.vue';
import { storageKeyLoginRedirect } from '@constants/storage-keys';
import SubmitButton from '@components/form/submit-button.vue';
import { useAppStore } from '@stores/app';
import { useVuelidate } from '@vuelidate/core';

interface Props {
	buttonColor?: string;
	componentId?: string;
}

interface LeadCaptureOcfState {
	email: string;
}

const props = withDefaults(defineProps<Props>(), {
	buttonColor: 'button-primary',
	componentId: ''
});

const state: LeadCaptureOcfState = reactive({
	email: ''
});

const rules = {
	email: { required, email }
};

const v$ = useVuelidate(rules, state);

const route = useRoute();
const router = useRouter();

const appStore = useAppStore();

const localStorageLeadCaptureKey = 'ocfLeadCapture';

const isWorking = ref(false);
const leadCaptured = ref(false);

const ocfLeadCaptured = computed((): string | null => getItemFromLocalStorage(localStorageLeadCaptureKey));

async function submitEmail(): Promise<void> {
	if (v$.value.$invalid) return;

	isWorking.value = true;
	try {
		const isLeadCaptured = ocfLeadCaptured.value === state.email;
		if (isLeadCaptured) {
			appStore.addToast({
				type: 'success',
				message: `You're on the waitlist.`
			});
		} else {
			const leadResponse: NonShoppingLeadCaptureResponse = await captureNonShoppingLead(preparePayload());
			setOcfLeadCaptured(state.email);

			if (leadResponse.existingUser) {
				setItemInLocalStorage('emailcapture', state.email);
				setItemInSessionStorage(storageKeyLoginRedirect, '/campaigns/opportunistic-credit-fund/join-waitlist');
				await router.push({ name: 'login' });
			} else {
				await router.push({ name: 'campaign-ocf-join-waitlist' });
			}
		}
	} catch (error) {
	} finally {
		isWorking.value = false;
	}
}

function preparePayload(): OcfLeadParams {
	return {
		email: state.email,
		mixpanelId: getDistinctId() || '',
		nonShoppingLeadCaptureType: 'OCF_LEAD',
		utmParams: {
			utmCampaign: route.query.utm_campaign ? route.query.utm_campaign.toString() : '',
			utmContent: route.query.utm_content ? route.query.utm_content.toString() : '',
			utmMedium: route.query.utm_medium ? route.query.utm_medium.toString() : '',
			utmSource: route.query.utm_source ? route.query.utm_source.toString() : ''
		}
	};
}

function setOcfLeadCaptured(leadEmail: string): void {
	setItemInLocalStorage(localStorageLeadCaptureKey, leadEmail);
	leadCaptured.value = true;
}

function setup(): void {
	leadCaptured.value = ocfLeadCaptured.value !== null;
}

setup();
</script>

<style lang="scss">
.lead-capture {
	.lead-capture__fieldset {
		.field__input {
			input {
				margin-top: 0 !important;
			}
		}
	}
}
</style>
